import {CallableDisposableStack, StackDisposable} from "../lib/disposable";
import {IBlobStore, MemoRemoteInterface} from "../IMemoRemote";
import {observable} from "../lib/signal";
import {slot} from "../lib/slot";
import {bufferToHex} from "../util/CoreUtils";
import {idbGet, idbPut, idbSearch, idbUpdate} from "../util/IDBUtils";
import {toBase64Url as b64encode, toBuffer as b64decode} from "b64u-lite";

export type BlobMetaInfo = {
    noUpload?: boolean,
    noUploadUntil?: { [remoteName: string]: number },
    access?: number,
    sent?: { [remoteName: string]: number },
    recvd?: { [remoteName: string]: number },
    size?: number,
    st?: number
};
const REMOTE_BLOB_NO_RECHECK_TIME = 30 * 24 * 60 * 60 * 1000;

@StackDisposable.Mixin
export class BlobStore implements IBlobStore, StackDisposable {
    declare [Symbol.dispose]: CallableDisposableStack;
    declare dtor: DisposableStack;

    private syncInProgress = false;

    @observable
    public accessor backingRemote: MemoRemoteInterface;

    public constructor(public readonly indexedDb: IDBDatabase) {
    }

    ///////////////////  BLOB STORE   ////////////////////////////////////////////////////////////////////

    @slot('backingRemote', $ => $.connectionEstablished)
    async onBackingRemoteConnected() {
        await this.syncBlobs(undefined);
    }

    private objectUrls: { [key: string]: string } = {};

    async getMediaURL(id: string): Promise<string> {
        const parts = id.split(':');
        const blobId = parts[parts.length - 1], mimeType = parts.length > 1 ? parts[0] : 'application/octet-stream';
        const cacheKey = mimeType + ':' + blobId;
        if (this.objectUrls[cacheKey]) return this.objectUrls[cacheKey];
        var url = URL.createObjectURL(new Blob([await this.retrieveBlob(blobId)], {type: mimeType}));
        this.objectUrls[cacheKey] = url;
        return url;
    }

    async storeBlob(data: Uint8Array, noNeedToSync: boolean = false): Promise<string> {
        if (!(data instanceof Uint8Array)) throw "invalid data parameter to LocalDb.storeBlob";
        const blobStoreHmacKey = await globalThis.crypto.subtle.importKey(
            "raw", new TextEncoder().encode("pinboard-blobStore"),
            {name: "HMAC", hash: {name: "SHA-256"}}, false, ["sign", "verify"]);
        const blobKeyRaw = new Uint8Array(await globalThis.crypto.subtle.sign('HMAC', blobStoreHmacKey, data));
        const blobKey = await globalThis.crypto.subtle.importKey("raw",
            blobKeyRaw, {name: "AES-CTR"}, false, ["encrypt"]);
        const encryptedData = new Uint8Array(await globalThis.crypto.subtle.encrypt({
            name: "AES-CTR",
            counter: new Uint8Array(16),
            length: 64
        }, blobKey, data));
        const encryptedHash = await this.writeBlobDataToDb(encryptedData, {});

        const blobIdWithKey = new Uint8Array(encryptedHash.byteLength + blobKeyRaw.byteLength);
        blobIdWithKey.set(encryptedHash, 0);
        blobIdWithKey.set(blobKeyRaw, encryptedHash.byteLength);

        if (!noNeedToSync) this.syncBlobs([bufferToHex(encryptedHash)]);
        return b64encode(blobIdWithKey);
    }

    private async writeBlobDataToDb(encryptedData: Uint8Array, meta: BlobMetaInfo): Promise<Uint8Array> {
        const encryptedHash = new Uint8Array(await globalThis.crypto.subtle.digest('sha-256', encryptedData));
        const mydata = {id: bufferToHex(encryptedHash), blob: encryptedData, st: Date.now()};
        await idbPut(this.indexedDb, "blobStore", mydata);
        await this.updateBlobMeta(mydata.id, Object.assign({size: mydata.blob.length, st: mydata.st}, meta));
        return encryptedHash;
    }

    async updateBlobMeta(id: string, meta: BlobMetaInfo) {
        return idbUpdate(this.indexedDb, "blobMeta", id, (item: (BlobMetaInfo & { id?: string }) | undefined) => {
            item = item || {id};
            console.debug("updating blob meta", item, id, meta);
            if ('noUpload' in meta) item.noUpload = meta.noUpload;
            if ('access' in meta) item.access = meta.access;
            if ('size' in meta) item.size = meta.size;
            if ('st' in meta) item.st = meta.st;
            if ('noUploadUntil' in meta) item.noUploadUntil = Object.assign({}, item.noUploadUntil, meta.noUploadUntil);
            if ('sent' in meta) item.sent = Object.assign({}, item.sent, meta.sent);
            if ('recvd' in meta) item.recvd = Object.assign({}, item.recvd, meta.recvd);
            return item;
        });
    }

    async syncBlobs(hashes: string[] | undefined) {
        if (this.syncInProgress) return;
        try {
            //using _ = ProgressBar.show('syncing files');
            this.syncInProgress = true;
            const syncTarget = 'pb';
            if (!hashes) {
                const now = Date.now();
                const meta = await idbSearch(this.indexedDb, 'blobMeta', (cursor) => cursor.value.size && !cursor.value.noUpload && (cursor.value.noUploadUntil?.[syncTarget] || 0) <= now);
                hashes = meta.map(m => m.id);
            }
            var exists = await this.backingRemote.checkBlobs(hashes);
            if (exists.length != hashes.length) throw "[DB/Blob/Sync] illegal check_blobs result";
            //var toUpload = hashes.filter((_, i) => !exists[i]);
            var i = 0;
            for (let i = 0; i < hashes.length; i++) {
                const hash = hashes[i];
                if (!exists[i]) {
                    console.info(`[DB/Blob/Sync] [${i} of ${exists.length}] syncing file`);
                    await this.backingRemote.storeBlob(await this.getBlobFromDb(hash));
                } else {
                    console.info(`[DB/Blob/Sync] [${i} of ${exists.length}] marking uploaded`);
                }
                await this.updateBlobMeta(hash, {
                    noUploadUntil: {[syncTarget]: this.noUploadUntil()},
                    sent: {[syncTarget]: +new Date()}
                });
            }
            //app.wndLog.print(`syncing files done`);
        } finally {
            this.syncInProgress = false;
        }
    }

    async retrieveBlobEx(mediaId: string): Promise<Blob> {
        const parts = mediaId.split(':');
        const blobId = parts[parts.length - 1], mimeType = parts.length > 1 ? parts[0] : 'application/octet-stream';
        const cacheKey = mimeType + ':' + blobId;
        return new Blob([await this.retrieveBlob(blobId)], {
            type: mimeType
        });
    }

    noUploadUntil() {
        return +new Date() + (REMOTE_BLOB_NO_RECHECK_TIME * (Math.random() + 0.7));
    }

    async retrieveBlob(blobId: string): Promise<Uint8Array> {
        if (blobId.length === 64) {
            const blobHash = blobId;
            await this.updateBlobMeta(blobHash, {access: +new Date()});
            const local = await this.getBlobFromDb(blobHash);
            if (local) return local;
            //using _ = ProgressBar.show('loading blob '+blobHash);
            console.log("retrieving blob from remote", blobHash);
            const remoteBlob = await this.backingRemote.retrieveBlob(blobHash);
            const storedBlobHash = await this.writeBlobDataToDb(remoteBlob, {
                noUploadUntil: {'pb': this.noUploadUntil()},
                recvd: {'pb': +new Date()}
            });
            if (bufferToHex(storedBlobHash) !== blobHash)
                throw (`requested blobhash ${blobHash}, got blob ${bufferToHex(storedBlobHash)}`);
            return remoteBlob;
        } else if (blobId.length === 86) {
            const binaryBlobId: Uint8Array = b64decode(blobId);
            const blobHash = bufferToHex(binaryBlobId.slice(0, 32));
            await this.updateBlobMeta(blobHash, {access: +new Date()});
            const blobKey = await globalThis.crypto.subtle.importKey('raw', binaryBlobId.slice(32, 64), {name: 'AES-CTR'}, true, ['decrypt']);
            let data = await this.getBlobFromDb(blobHash);
            if (!data) {
                //using _ = ProgressBar.show('loading blob '+blobHash);
                console.log("retrieving blob from remote", blobHash);
                data = await this.backingRemote.retrieveBlob(blobHash);
                const storedBlobHash = await this.writeBlobDataToDb(data, {
                    noUploadUntil: {'pb': this.noUploadUntil()},
                    recvd: {'pb': +new Date()}
                });
                if (bufferToHex(storedBlobHash) !== blobHash)
                    throw (`requested blobhash ${blobHash}, got blob ${bufferToHex(storedBlobHash)}`);
            }
            return new Uint8Array(await globalThis.crypto.subtle.decrypt({
                name: 'AES-CTR',
                counter: new Uint8Array(16),
                length: 64
            }, blobKey, data));
        } else {
            throw "invalid blobId";
        }
    }

    async getBlobFromDb(blobHash: string): Promise<Uint8Array> {
        return (await idbGet<any>(this.indexedDb, "blobStore", [blobHash]))?.blob;
    }

    getAllBlobIds(): Promise<string[]> {
        return new Promise((resolve, reject) => {
            const ids: string[] = [];
            const rq = this.indexedDb
                .transaction("blobStore")
                .objectStore("blobStore")
                .openKeyCursor();
            rq.onsuccess = () => {
                const cursor = rq.result;
                if (cursor) {
                    ids.push(cursor.key[0]);
                    cursor.continue();
                } else {
                    resolve(ids);
                }
            };
        });
    }

}
